import React from "react";
import FluentTooltip from "../../../components/FluentTooltip";
import { formatDate } from "../../../utils/utils";
import AttachmentsCell from "./columnCells/AttachmentsCell";
import ClientName from "./columnCells/ClientName";
import CountryCode from "./columnCells/CountryCode";
import FleetName from "./columnCells/FleetName";
import MainInsurerCell from "./columnCells/MainInsurerCell";

const renderInsurer = (
  hasMultiple: boolean,
  name: string,
  number: string | number,
) => (hasMultiple ? "* " : "") + (name ? name + " (" + number + ")" : "-");

export const defaultColumns: any[] = [
  {
    key: "fleetName",
    fieldName: "fleetName",
    labelKey: "bfm.fleetForm.fleetName.label",
    name: "bfm.fleetForm.fleetName.label",
    maxWidth: 250,
    minWidth: 250,
    isVisible: true,
    isSortable: true,
    iconName: "Sort",
    align: "left",
    excel: { width: 250 },
    onRender: (row) => {
      if (!row) return null;
      return (
        <FleetName
          fleetName={row?.fleetName}
          fleetId={row?.fleetId}
          isCancelled={row?.isCancelled}
        />
      );
    },
  },
  {
    key: "options",
    fieldName: "options",
    labelKey: "",
    name: "",
    maxWidth: 20,
    minWidth: 20,
    isVisible: true,
    excel: { isVisible: false },
  },
  {
    key: "attachments",
    fieldName: "attachments",
    labelKey: "bfm.vehicle.tabs.attachments",
    name: "bfm.vehicle.tabs.attachments",
    maxWidth: 80,
    minWidth: 80,
    isVisible: true,
    isSortable: false,
    align: "center",
    excel: {
      width: 200,
      render: ({ row }) =>
        (row.attachments ?? []).map((a) => a.attachmentName).join(", "),
    },
    onRender: (row) => <AttachmentsCell attachments={row?.attachments} />,
  },
  {
    key: "countryCode",
    fieldName: "countryCode",
    labelKey: "bfm.country.label",
    name: "bfm.country.label",
    maxWidth: 70,
    isSortable: true,
    iconName: "Sort",
    minWidth: 70,
    isVisible: true,
    align: "left",
    excel: {
      width: 70,
      render: ({ t, taxonomy, row }) =>
        t(taxonomy.Country.byId[row.countryCode]?.code),
    },
    onRender: (row) => <CountryCode code={row?.countryCode} />,
  },
  {
    key: "clientName",
    fieldName: "clientName",
    labelKey: "bfm.fleetForm.client.label",
    name: "bfm.fleetForm.client.label",
    maxWidth: 250,
    minWidth: 250,
    isVisible: true,
    isSortable: true,
    align: "left",
    excel: {
      width: 250,
      render: ({ row }) =>
        row.clientName +
        (row.webBasePartnerNumber ? " (" + row.webBasePartnerNumber + ")" : ""),
    },
    onRender: (row) => (
      <ClientName
        clientName={row?.clientName}
        webBasePartnerNumber={row?.webBasePartnerNumber}
        isFormerClient={row?.isFormerClient}
      />
    ),
    iconName: "Sort",
  },
  {
    key: "motorSpecialistName",
    fieldName: "motorSpecialistName",
    labelKey: "bfm.fleetForm.motorSpecialist.label",
    name: "bfm.fleetForm.motorSpecialist.label",
    maxWidth: 230,
    minWidth: 230,
    isVisible: true,
    isSortable: true,
    iconName: "Sort",
    align: "left",
    excel: {
      width: 230,
      render: ({ row }) =>
        row.motorSpecialistName
          ? row.motorSpecialistName +
            (row.motorSpecialistEmail
              ? " (" + row.motorSpecialistEmail + ")"
              : "")
          : "",
    },
    onRender: (row) => {
      if (!row?.motorSpecialistName) return null;
      const content =
        row.motorSpecialistName +
        (row.motorSpecialistEmail ? " (" + row.motorSpecialistEmail + ")" : "");
      return <FluentTooltip content={content}>{content}</FluentTooltip>;
    },
  },
  {
    key: "mainMtplInsurerPartnerName",
    fieldName: "mainMtplInsurerPartnerName",
    labelKey: "bfm.fleetForm.mainMtplInsurerPartnerName.label",
    name: "bfm.fleetForm.mainMtplInsurerPartnerName.label",
    maxWidth: 230,
    minWidth: 230,
    isVisible: true,
    isSortable: true,
    align: "left",
    excel: {
      width: 230,
      render: ({ row }) =>
        renderInsurer(
          row.hasMultipleMtplInsurers,
          row.mainMtplInsurerPartnerName,
          row.mainMtplInsurerPartnerNumber,
        ),
    },
    onRender: (row) => (
      <MainInsurerCell
        hasMultipleInsurers={row.hasMultipleMtplInsurers}
        mainPartnerName={row?.mainMtplInsurerPartnerName}
        mainPartnerNumber={row?.mainMtplInsurerPartnerName}
      />
    ),
    iconName: "Sort",
  },
  {
    key: "mainHullInsurerPartnerName",
    fieldName: "mainHullInsurerPartnerName",
    labelKey: "bfm.fleetForm.mainHullInsurerPartnerName.label",
    name: "bfm.fleetForm.mainHullInsurerPartnerName.label",
    maxWidth: 230,
    minWidth: 230,
    isVisible: true,
    isSortable: true,
    align: "left",
    excel: {
      width: 230,
      render: ({ row }) =>
        renderInsurer(
          row.hasMultipleHullInsurers,
          row.mainHullInsurerPartnerName,
          row.mainHullInsurerPartnerNumber,
        ),
    },
    onRender: (row) => (
      <MainInsurerCell
        hasMultipleInsurers={row.hasMultipleHullInsurers}
        mainPartnerName={row?.mainHullInsurerPartnerName}
        mainPartnerNumber={row?.mainHullInsurerPartnerNumber}
      />
    ),
    iconName: "Sort",
  },
  {
    key: "mainFleetLegalInsurerPartnerName",
    fieldName: "mainFleetLegalInsurerPartnerName",
    labelKey: "bfm.fleetForm.mainFleetLegalInsurerPartnerName.label",
    name: "bfm.fleetForm.mainFleetLegalInsurerPartnerName.label",
    maxWidth: 230,
    minWidth: 230,
    isVisible: true,
    isSortable: true,
    align: "left",
    excel: {
      width: 230,
      render: ({ row }) =>
        renderInsurer(
          row.hasMultipleFleetLegalInsurers,
          row.mainFleetLegalInsurerPartnerName,
          row.mainFleetLegalInsurerPartnerNumber,
        ),
    },
    onRender: (row) => (
      <MainInsurerCell
        hasMultipleInsurers={row.hasMultipleFleetLegalInsurers}
        mainPartnerName={row?.mainFleetLegalInsurerPartnerName}
        mainPartnerNumber={row?.mainFleetLegalInsurerPartnerNumber}
      />
    ),
    iconName: "Sort",
  },
  /*
  {
    key: "mainHullOnLandInsurerPartnerName",
    fieldName: "mainHullOnLandInsurerPartnerName",
    labelKey: "bfm.fleetForm.mainHullOnLandInsurerPartnerName.label",
    name: "bfm.fleetForm.mainHullOnLandInsurerPartnerName.label",
    maxWidth: 230,
    minWidth: 230,
    isVisible: true,
    isSortable: true,
    align: "left",
    excel: {
      width: 230,
      render: ({ row }) =>
        renderInsurer(
          row.hasMultipleHullOnLandInsurers,
          row.mainHullOnLandInsurerPartnerName,
          row.mainHullOnLandInsurerPartnerNumber,
        ),
    },
    onRender: (row) => (
      <MainInsurerCell
        hasMultipleInsurers={row.hasMultipleHullOnLandInsurers}
        mainPartnerName={row?.mainHullOnLandInsurerPartnerName}
        mainPartnerNumber={row?.mainHullOnLandInsurerPartnerNumber}
      />
    ),
    iconName: "Sort",
  },
  {
    key: "mainMachineryBreakdownInsurerPartnerName",
    fieldName: "mainMachineryBreakdownInsurerPartnerName",
    labelKey: "bfm.fleetForm.mainMachineryBreakdownInsurerPartnerName.label",
    name: "bfm.fleetForm.mainMachineryBreakdownInsurerPartnerName.label",
    maxWidth: 230,
    minWidth: 230,
    isVisible: true,
    isSortable: true,
    align: "left",
    excel: {
      width: 230,
      render: ({ row }) =>
        renderInsurer(
          row.hasMultipleMachineryBreakdownInsurers,
          row.mainMachineryBreakdownInsurerPartnerName,
          row.mainMachineryBreakdownInsurerPartnerNumber,
        ),
    },
    onRender: (row) => (
      <MainInsurerCell
        hasMultipleInsurers={row.hasMultipleMachineryBreakdownInsurers}
        mainPartnerName={row?.mainMachineryBreakdownInsurerPartnerName}
        mainPartnerNumber={row?.mainMachineryBreakdownInsurerPartnerNumber}
      />
    ),
    iconName: "Sort",
  },
*/
  {
    key: "businessYearStartMonth",
    fieldName: "businessYearStartMonth",
    labelKey: "bfm.fleetForm.fiscalDate.label",
    name: "bfm.fleetForm.fiscalDate.label",
    maxWidth: 90,
    minWidth: 90,
    isSortable: true,
    iconName: "Sort",
    isVisible: true,
    align: "left",
    excel: {
      width: 90,
      render: ({ row }) =>
        row.businessYearStartDay + "." + row.businessYearStartMonth,
    },
    onRender: (row) =>
      row?.businessYearStartDay + "." + row?.businessYearStartMonth,
  },
  {
    key: "dueMonth",
    fieldName: "dueMonth",
    labelKey: "bfm.fleetForm.dueDate.label",
    name: "bfm.fleetForm.dueDate.label",
    maxWidth: 90,
    minWidth: 90,
    isSortable: true,
    iconName: "Sort",
    isVisible: true,
    align: "left",
    excel: {
      width: 90,
      render: ({ row }) => row.dueDay + "." + row.dueMonth,
    },
    onRender: (row) => row?.dueDay + "." + row?.dueMonth,
  },
  {
    key: "fleetCreatedAt",
    fieldName: "fleetCreatedAt",
    labelKey: "bfm.fleetForm.fleetCreatedAt.label",
    name: "bfm.fleetForm.fleetCreatedAt.label",
    maxWidth: 90,
    minWidth: 90,
    isVisible: true,
    isSortable: true,
    align: "left",
    excel: {
      width: 90,
      render: ({ row }) =>
        row.fleetCreatedAt ? formatDate(new Date(row.fleetCreatedAt)) : "",
    },
    onRender: (row) =>
      row?.fleetCreatedAt && formatDate(new Date(row?.fleetCreatedAt)),
    iconName: "Sort",
  },
  {
    key: "fleetCreatedByUserName",
    fieldName: "fleetCreatedByUserName",
    labelKey: "bfm.fleetForm.fleetCreatedBy.label",
    name: "bfm.fleetForm.fleetCreatedBy.label",
    maxWidth: 130,
    minWidth: 130,
    isVisible: true,
    isSortable: true,
    iconName: "Sort",
    align: "left",
    excel: {
      width: 130,
      render: ({ row }) =>
        row.fleetCreatedByUserName ?? row.fleetCreatedByUserId,
    },
    onRender: (row) => (
      <FluentTooltip
        content={row?.fleetCreatedByUserName ?? row?.fleetCreatedByUserId}
      >
        {row?.fleetCreatedByUserName ?? row?.fleetCreatedByUserId}
      </FluentTooltip>
    ),
  },

  {
    key: "fleetPolicyNumber",
    fieldName: "fleetPolicyNumber",
    labelKey: "bfm.fleetForm.fleetPolicyNumber.label",
    name: "bfm.fleetForm.fleetPolicyNumber.label",
    maxWidth: 110,
    minWidth: 110,
    isVisible: true,
    isSortable: true,
    iconName: "Sort",
    align: "left",
    excel: { width: 110 },
    onRender: (row) => {
      if (!row) return null;
      return row?.fleetPolicyNumber;
    },
  },
  {
    key: "fleetLegalPolicyNumber",
    fieldName: "fleetLegalPolicyNumber",
    labelKey: "bfm.fleetForm.fleetLegalPolicyNumber.label",
    name: "bfm.fleetForm.fleetLegalPolicyNumber.label",
    maxWidth: 200,
    minWidth: 200,
    isVisible: true,
    isSortable: true,
    iconName: "Sort",
    align: "left",
    excel: { width: 200 },
    onRender: (row) => {
      if (!row) return null;
      return row?.fleetLegalPolicyNumber;
    },
  },
  {
    key: "fleetComment",
    fieldName: "fleetComment",
    labelKey: "bfm.fleetForm.fleetComment.label",
    name: "bfm.fleetForm.fleetComment.label",
    maxWidth: 250,
    minWidth: 250,
    isVisible: true,
    isSortable: true,
    iconName: "Sort",
    align: "left",
    excel: { width: 250 },
    onRender: (row) => (
      <FluentTooltip content={row?.fleetComment ?? row?.fleetComment}>
        {row?.fleetComment ?? row?.fleetComment}
      </FluentTooltip>
    ),
  },
];
